const MAX_THUMBNAIL_WIDTH = 800
const MAX_THUMBNAIL_HEIGHT = 600
const MIN_SIZE_FOR_THUMBNAIL = 32 * 1024 // 32KB

export type ThumbnailResult = {
  uri: string // blob: URL
  width: number
  height: number
  mimeType: string
  size: number
}

/**
 * Generate a thumbnail for an image.
 * Returns null if the original is too small to benefit from a thumbnail.
 *
 * Web implementation using canvas.
 */
export async function generateThumbnail(
  uri: string,
  originalWidth: number,
  originalHeight: number,
  originalSize?: number,
): Promise<ThumbnailResult | null> {
  if (originalSize !== undefined && originalSize < MIN_SIZE_FOR_THUMBNAIL) {
    return null
  }

  const {width, height} = fitWithin(
    originalWidth,
    originalHeight,
    MAX_THUMBNAIL_WIDTH,
    MAX_THUMBNAIL_HEIGHT,
  )

  if (width >= originalWidth && height >= originalHeight) {
    return null
  }

  const img = await loadImage(uri)
  const canvas =
    typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(width, height)
      : document.createElement('canvas')

  if ('width' in canvas) {
    canvas.width = width
    canvas.height = height
  }

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Failed to get canvas context')
  ;(ctx as CanvasRenderingContext2D).drawImage(img, 0, 0, width, height)

  const blob = await canvasToBlob(canvas, 'image/jpeg', 0.7)
  const blobUri = URL.createObjectURL(blob)

  return {
    uri: blobUri,
    width,
    height,
    mimeType: 'image/jpeg',
    size: blob.size,
  }
}

export function shouldIncludeThumbnail(
  originalSize: number,
  thumbnailSize: number,
): boolean {
  const reduction = originalSize - thumbnailSize
  return reduction >= 1024 * 1024 || reduction / originalSize >= 0.1
}

function fitWithin(
  w: number,
  h: number,
  maxW: number,
  maxH: number,
): {width: number; height: number} {
  if (w <= maxW && h <= maxH) return {width: w, height: h}
  const scale = Math.min(maxW / w, maxH / h)
  return {
    width: Math.round(w * scale),
    height: Math.round(h * scale),
  }
}

function loadImage(uri: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to load image'))
    img.crossOrigin = 'anonymous'
    img.src = uri
  })
}

function canvasToBlob(
  canvas: HTMLCanvasElement | OffscreenCanvas,
  type: string,
  quality: number,
): Promise<Blob> {
  if (canvas instanceof OffscreenCanvas) {
    return canvas.convertToBlob({type, quality})
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob =>
        blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')),
      type,
      quality,
    )
  })
}
